<template>
  <!-- START REGULAR SEARCH INPUT -->
  <div class="relative" :class="[isMainMenu ? 'hidden' : 'block']">
    <!-- START SEARCH RESULTS -->
    <div
      v-if="showSearchResults"
      v-click-outside="closeSearchResults"
      class="
        rounded
        absolute
        shadow-lg
        border border-brand-green
        z-20
        bg-white
        w-full
      "
    >
      <div class="relative rounded">
        <SearchIcon
          class="
            w-5
            h-5
            text-brand-green
            absolute
            top-2.5
            md:top-3
            left-2
            md:left-4
          "
        />
        <input
          type="text"
          ref="search"
          class="
            w-full
            py-2
            pl-9
            md:pl-12
            placeholder-brand-mid-gray
            border-0
            focus:outline-none focus:ring-0
            inline-block
            caret-brand-green
            text-sm
            md:text-base
            rounded
          "
          :placeholder="placeholder"
          v-model="searchTerm"
          @keyup.enter="handleSearch"
        />
        <div
          class="
            py-1
            px-1
            absolute
            top-2
            right-3
            md:right-6
            rounded
            bg-brand-background-light-gray
            cursor-pointer
          "
          @click="clearSearch"
          v-if="searchTerm"
        >
          <p class="text-xs md:text-sm text-brand-dark-gray">clear</p>
        </div>
      </div>
      <div class="mx-auto w-11/12"><Line /></div>
      <div class="bg-white px-5 pt-1 pb-3 rounded">
        <template v-if="!searchTerm">
          <p>Search</p>
          <p class="text-brand-mid-gray text-sm py-1">
            {{ searchResultsPlaceholder }}
          </p>
        </template>
        <template v-if="searchTerm">
          <p>Recent searches</p>
          <div class="flex flex-col py-1">
            <div
              class="flex flex-row justify-between py-1"
              v-for="(recent, index) in recentSearches"
              :key="recent"
            >
              <div
                class="flex flex-row cursor-pointer"
                @click="handleSearch(recent)"
              >
                <div class="flex items-center mr-3">
                  <ClockIcon class="w-5 h-5 text-brand-mid-gray" />
                </div>
                <div class="flex items-center">
                  <p class="text-purple-600">{{ recent }}</p>
                </div>
              </div>
              <div
                class="flex items-center cursor-pointer"
                @click="removeRecentSearch(index)"
              >
                <XIcon class="w-5 h-5 text-brand-mid-gray" />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <!-- END SEARCH RESULTS -->

    <!-- START SEARCH INPUT DEFAULT -->
    <div class="relative">
      <SearchIcon
        class="
          w-5
          h-5
          text-brand-green
          absolute
          top-2.5
          md:top-3
          left-2
          md:left-4
        "
      />
      <div
        class="
          w-full
          py-2
          pl-9
          md:pl-12
          border border-brand-background-light-blue
          shadow-sm
          rounded
          text-sm
          md:text-base
        "
        :class="[searchTerm ? 'text-brand-black' : 'text-brand-dark-gray']"
        @click="openSearchResults"
      >
        {{ searchTerm || placeholder }}
      </div>
      <div
        class="
          py-1
          px-1
          absolute
          top-2
          right-3
          md:right-6
          rounded
          bg-brand-background-light-gray
          cursor-pointer
        "
        @click="clearSearch"
        v-if="searchTerm"
      >
        <p class="text-xs md:text-sm text-brand-dark-gray">clear</p>
      </div>
    </div>
    <!-- END SEARCH INPUT DEFAULT -->
  </div>
  <!-- END REGULAR SEARCH INPUT -->

  <!-- START MAIN MENU MOBILE SEARCH -->
  <div class="flex-col relative" :class="[isMainMenu ? 'flex' : 'hidden']">
    <!-- START MAIN MENU MOBILE SEARCH INPUT -->
    <div class="relative px-1">
      <SearchIcon class="w-5 h-5 text-brand-green absolute top-2 left-5" />
      <input
        type="text"
        ref="search"
        class="
          w-full
          py-2
          pl-10
          placeholder-brand-mid-gray
          border border-brand-mid-gray
          focus:outline-none focus:ring-0
          inline-block
          caret-brand-green
          text-xs
          rounded
        "
        :placeholder="placeholder"
        v-model="searchTerm"
        @keyup.enter="handleSearch"
      />
      <div
        class="
          py-1
          px-1
          absolute
          top-1.5
          right-3
          rounded
          bg-brand-background-light-gray
          cursor-pointer
        "
        @click="clearSearch"
        v-if="searchTerm"
      >
        <p class="text-xs text-brand-dark-gray">clear</p>
      </div>
    </div>
    <!-- END MAIN MENU MOBILE SEARCH INPUT -->

    <!-- START MAIN MENU MOBILE SEARCH RESULT -->
    <template v-if="searchTerm">
      <div v-if="isMainMenu" class="px-3 py-3">
        <p class="text-brand-green text-sm pb-3" @click="handleSearch">
          See all results for "{{ searchTerm }}"
        </p>
        <template v-if="recentSearches.length">
          <p class="text-sm">Recent searches</p>
          <div class="flex flex-col py-1">
            <div
              class="flex flex-row justify-between py-1"
              v-for="(recent, index) in recentSearches"
              :key="recent"
            >
              <div
                class="flex flex-row cursor-pointer"
                @click="handleSearch(recent)"
              >
                <div class="flex items-center mr-3">
                  <ClockIcon class="w-5 h-5 text-brand-mid-gray" />
                </div>
                <div class="flex items-center">
                  <p class="text-purple-600 text-sm">{{ recent }}</p>
                </div>
              </div>
              <div
                class="flex items-center cursor-pointer"
                @click="removeRecentSearch(index)"
              >
                <XIcon class="w-5 h-5 text-brand-mid-gray" />
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
    <!-- END MAIN MENU MOBILE SEARCH RESULT -->
  </div>
  <!-- END MAIN MENU MOBILE SEARCH -->
</template>
<script>
import _ from "lodash"

import { SearchIcon, ClockIcon, XIcon } from "@heroicons/vue/outline"

export default {
  components: {
    SearchIcon,
    ClockIcon,
    XIcon
  },
  emits: ["search"],
  props: {
    placeholder: {
      type: String,
      default: "Search for an applicant, property or agent"
    },
    searchResultsPlaceholder: {
      type: String,
      default:
        "Try searching for an applicant, property, or an agent whose received an application."
    },
    isMainMenu: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.recentSearches = JSON.parse(
      localStorage.getItem("recent_search") || "[]"
    ).slice(0, 5)

    if (this.initial) {
      this.searchTerm = this.initial
    }
  },
  data() {
    return {
      showSearchResults: false,
      searchTerm: "",
      recentSearches: []
    }
  },
  methods: {
    openSearchResults() {
      this.showSearchResults = true
      setTimeout(() => {
        this.$refs.search.focus()
      }, 100)
    },
    closeSearchResults() {
      this.showSearchResults = false
    },
    clearSearch() {
      this.searchTerm = ""
      this.handleSearch()
    },
    handleSearch(term) {
      if (typeof term === "string") this.searchTerm = term

      this.recentSearches = _.uniq([this.searchTerm, ...this.recentSearches])
        .filter(Boolean)
        .slice(0, 5)

      localStorage.setItem("recent_search", JSON.stringify(this.recentSearches))

      this.$emit("search", this.searchTerm)

      this.closeSearchResults()
    },
    removeRecentSearch(index) {
      this.recentSearches.splice(index, 1)
      localStorage.setItem("recent_search", JSON.stringify(this.recentSearches))
    }
  }
}
</script>
